/**
 * POST `admin/clients` — создать аккаунт клиники/соло (ADMIN_SPEC §5).
 * Гейтвей атомарно заводит клинику/соло + владельца + роли + триал 30 дней
 * и возвращает временный пароль владельца — показываем один раз.
 *
 * Города UI держит именами — в cityId переводит справочник getCities.
 * Специализация соло (`specializationId`) в схеме есть, но форма её пока
 * не спрашивает — поэтому не отправляем.
 *
 * `applicationId` связывает аккаунт с заявкой в очереди: бэк проставит в
 * ней `convertedClinicId`, и очередь перестанет предлагать заводить
 * клиента второй раз. Без него ручка работает как раньше — поэтому
 * создание «с нуля» из раздела «Клиенты» ничего не теряет.
 *
 * Ошибки, которые может вернуть ручка (разводим по коду в теле, не по
 * тексту): 409 `phone_already_used`, 409 `application_already_converted`
 * (+ `clinicId`), 422 `application_not_found`. Порядок проверок на бэке —
 * телефон раньше заявки, поэтому повтор уже выполненного запроса придёт
 * как `phone_already_used`, без `clinicId`.
 */

import {markApplicationConvertedMock} from '@services/applications/mocks';
import {apiEnabled, http} from '@services/utils';
import {toE164} from '@utils/phone';

import {getCities} from '../getCities';
import type {ClinicType} from '../types';

export type CreateAccountInput = {
  type: ClinicType;
  /** Телефон-логин (админа для клиники, врача для соло). */
  adminPhone: string;
  // clinic
  clinicName?: string;
  city?: string;
  language?: string;
  address?: string;
  businessPhone?: string;
  adminFullName?: string;
  seats?: number;
  // solo
  fullName?: string;
  /** Заявка из очереди, по которой заводим клиента. */
  applicationId?: string;
};

export type CreateAccountResult = {
  ok: true;
  id: string;
  /** Временный пароль владельца — показать один раз; в моке демо-значение */
  tempPassword: string | null;
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const createAccount = async (input: CreateAccountInput): Promise<CreateAccountResult> => {
  if (!apiEnabled) {
    await sleep(500);
    if (import.meta.env.DEV) console.info('[clients] createAccount (mock)', input);
    const id = `new-${Date.now()}`;
    /* Мок тоже закрывает заявку — иначе на моках очередь продолжала бы
       предлагать завести клиента, которого уже «завели». */
    if (input.applicationId !== undefined) {
      markApplicationConvertedMock(input.applicationId, id);
    }
    return {ok: true, id, tempPassword: 'demo-4821'};
  }

  const isClinic = input.type === 'clinic';
  const cityId = input.city
    ? ((await getCities()).find((c) => c.name === input.city)?.id ?? null)
    : null;

  const raw = await http<{id?: string; tempPassword?: string}>('admin/clients', {
    method: 'POST',
    body: {
      type: input.type,
      adminFullName: (isClinic ? input.adminFullName : input.fullName) ?? '',
      adminPhone: toE164(input.adminPhone),
      seats: isClinic ? (input.seats ?? null) : null,
      cityId,
      name: isClinic ? (input.clinicName ?? null) : null,
      address: input.address?.trim() ? input.address.trim() : null,
      businessPhone: input.businessPhone?.trim() ? toE164(input.businessPhone) : null,
      language: input.language === 'Кыргызча' ? 'kg' : 'ru',
      /* Поле необязательное: отправляем только когда заводим по заявке,
         иначе бэк получил бы null там, где мог бы ничего не получить. */
      ...(input.applicationId !== undefined && {applicationId: input.applicationId}),
    },
    skipCaseConversion: true,
  });

  return {
    ok: true,
    id: typeof raw.id === 'string' ? raw.id : '',
    tempPassword: typeof raw.tempPassword === 'string' ? raw.tempPassword : null,
  };
};
